import React from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n, { setLanguage } from "@/i18n";
import { Leaf, LogOut, FileText, Users, Stethoscope, BookOpen, Activity } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin", doctor: "Doctor / Healthcare", participant: "Participant", user_manager: "User Manager",
};

export default function AppShell({ children, right }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const cur = i18n.language;
  const isClinical = user?.role === "admin" || user?.role === "doctor";
  const showNav = isClinical || user?.role === "user_manager";
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="sticky top-0 z-30 border-b border-[#E5DFD3] bg-white/90 backdrop-blur no-print">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4 text-emerald-900">
            <div className="flex items-center gap-2">
              <Leaf className="h-5 w-5" />
              <span className="font-serif text-lg font-semibold">Ayurtoday</span>
            </div>
            {showNav && (
              <nav className="hidden items-center gap-1 sm:flex">
                {isClinical && (
                  <button onClick={() => nav("/panel")} data-testid="nav-panel"
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50">
                    <Stethoscope className="h-4 w-4" /> {t(user?.role === "admin" ? "Participants" : "My caseload")}
                  </button>
                )}
                {isClinical && (
                  <button onClick={() => nav("/trends")} data-testid="nav-trends"
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50">
                    <Activity className="h-4 w-4" /> {t("Weekly trends")}
                  </button>
                )}
                {isClinical && (
                  <button onClick={() => nav("/reference")} data-testid="nav-reference"
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50">
                    <BookOpen className="h-4 w-4" /> {t("Reference")}
                  </button>
                )}
                {user?.role !== "doctor" && (
                  <button onClick={() => nav("/users")} data-testid="nav-users"
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50">
                    <Users className="h-4 w-4" /> {t("Users")}
                  </button>
                )}
                <button onClick={() => nav("/submissions")} data-testid="nav-submissions"
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50">
                  <FileText className="h-4 w-4" /> {t("Assessments")}
                </button>
              </nav>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex overflow-hidden rounded-md border border-[#E5DFD3] text-xs" data-testid="lang-toggle">
              {["en", "ta"].map((lng) => (
                <button key={lng} data-testid={`lang-${lng}`} onClick={() => setLanguage(lng)}
                  className={`px-2.5 py-1 font-medium transition-colors ${cur === lng ? "bg-emerald-800 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}>
                  {lng === "en" ? "EN" : "தமிழ்"}
                </button>
              ))}
            </div>
            {right}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-medium text-stone-800" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-stone-400">{t(ROLE_LABEL[user?.role] || "")}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn"
              className="flex items-center gap-1.5 rounded-md border border-[#E5DFD3] bg-white px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50">
              <LogOut className="h-4 w-4" /><span className="hidden sm:inline">{t("Logout")}</span>
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
}
